import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Filter, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { produtos, empresa } from '../data/mock';

const Loja = () => {
  const [filtro, setFiltro] = useState('todos');
  const [busca, setBusca] = useState('');

  const produtosFiltrados = produtos.filter((produto) => {
    const matchBusca = produto.nome.toLowerCase().includes(busca.toLowerCase()) ||
                       produto.descricao.toLowerCase().includes(busca.toLowerCase());
    
    if (filtro === 'todos') return matchBusca;
    if (filtro === 'notebooks') return matchBusca && produto.nome.toLowerCase().includes('notebook');
    if (filtro === 'desktops') return matchBusca && produto.nome.toLowerCase().includes('pc');
    return matchBusca;
  });

  const handleWhatsApp = (produto) => {
    const mensagem = `Olá! Tenho interesse no ${produto.nome}.\n\nPreço à vista: R$ ${produto.preco_vista.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}\nParcelado: ${produto.parcelas}x de R$ ${(produto.preco_parcelado / produto.parcelas).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} sem juros\n\nGostaria de mais informações.`;
    const texto = encodeURIComponent(mensagem);
    window.open(`${empresa.contato.whatsapp_link}?text=${texto}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-purple-700 via-blue-700 to-cyan-600 text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-4">Nossa Loja</h1>
            <p className="text-xl opacity-90">
              Notebooks e computadores novos e seminovos com garantia. Parcelamento em até 12x sem juros.
            </p>
          </div>
        </div>
      </section>

      {/* Filtros */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={filtro === 'todos' ? 'default' : 'outline'}
                onClick={() => setFiltro('todos')}
                className={filtro === 'todos' ? 'bg-gradient-to-r from-purple-600 to-blue-600' : ''}
              >
                Todos
              </Button>
              <Button
                variant={filtro === 'notebooks' ? 'default' : 'outline'}
                onClick={() => setFiltro('notebooks')}
                className={filtro === 'notebooks' ? 'bg-gradient-to-r from-purple-600 to-blue-600' : ''}
              >
                Notebooks
              </Button>
              <Button
                variant={filtro === 'desktops' ? 'default' : 'outline'}
                onClick={() => setFiltro('desktops')}
                className={filtro === 'desktops' ? 'bg-gradient-to-r from-purple-600 to-blue-600' : ''}
              >
                Desktops
              </Button>
            </div>

            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Buscar produtos..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Produtos */}
      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {produtosFiltrados.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-700 mb-2">
                Nenhum produto encontrado
              </h3>
              <p className="text-gray-500">
                Tente ajustar seus filtros ou busca
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {produtosFiltrados.map((produto) => (
                <Card
                  key={produto.id}
                  className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
                >
                  <Link to={`/produto/${produto.id}`}>
                    <div className="aspect-[4/3] bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden cursor-pointer">
                      <img
                        src={produto.imagem}
                        alt={produto.nome}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute top-4 right-4">
                        <Badge className="bg-blue-600 text-white">Disponível</Badge>
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <span className="text-white text-lg font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                          Ver Detalhes
                        </span>
                      </div>
                    </div>
                  </Link>

                  <CardContent className="p-6">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">
                      {produto.nome}
                    </h3>
                    <p className="text-gray-600 mb-4">{produto.descricao}</p>

                    <div className="space-y-3 mb-6 bg-slate-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Processador:</span>
                        <span className="font-semibold text-slate-900">{produto.processador}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Memória RAM:</span>
                        <span className="font-semibold text-slate-900">{produto.ram}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Armazenamento:</span>
                        <span className="font-semibold text-slate-900">{produto.armazenamento}</span>
                      </div>
                      {produto.tela && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Tela:</span>
                          <span className="font-semibold text-slate-900">{produto.tela}</span>
                        </div>
                      )}
                      {produto.placa_video && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Placa de Vídeo:</span>
                          <span className="font-semibold text-slate-900">{produto.placa_video}</span>
                        </div>
                      )}
                      {produto.placa_video_dedicada && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">GPU Dedicada:</span>
                          <span className="font-semibold text-slate-900">{produto.placa_video_dedicada}</span>
                        </div>
                      )}
                      {produto.placa_video_integrada && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">GPU Integrada:</span>
                          <span className="font-semibold text-slate-900">{produto.placa_video_integrada}</span>
                        </div>
                      )}
                      {produto.teclado_numerico && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Teclado Numérico:</span>
                          <span className="font-semibold text-slate-900">{produto.teclado_numerico}</span>
                        </div>
                      )}
                      {produto.extras && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Extras:</span>
                          <span className="font-semibold text-slate-900">{produto.extras}</span>
                        </div>
                      )}
                      {produto.observacoes && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Observações:</span>
                          <span className="font-semibold text-red-600">{produto.observacoes}</span>
                        </div>
                      )}
                    </div>

                    <div className="border-t pt-4">
                      <div className="mb-4">
                        <div className="flex items-baseline gap-2 mb-1">
                          <p className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-cyan-600 bg-clip-text text-transparent">
                            R$ {produto.preco_vista.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </p>
                          <span className="text-base text-gray-700 font-medium">à vista</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-1">
                          ou R$ {produto.preco_parcelado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} em {produto.parcelas}x sem juros
                        </p>
                        <p className="text-sm text-gray-600">
                          {produto.parcelas}x de <span className="font-semibold text-slate-900">
                            R$ {(produto.preco_parcelado / produto.parcelas).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        </p>
                      </div>

                      <Button
                        className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                        onClick={() => handleWhatsApp(produto)}
                      >
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Comprar via WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Informações Adicionais */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🛡️</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Garantia</h3>
              <p className="text-gray-600">
                Todos os produtos com garantia de fábrica ou da loja
              </p>
            </div>

            <div className="p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">💳</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Parcelamento</h3>
              <p className="text-gray-600">
                Parcele em até 12x sem juros no cartão de crédito
              </p>
            </div>

            <div className="p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🤝</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Atendimento</h3>
              <p className="text-gray-600">
                Suporte especializado antes e após a compra
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Loja;
